import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { Integration } from '../types';
import { PageHeader, LoadingSpinner, Button, Badge } from '../components/ui';

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { authStatus } = useAuth();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);

  const isProUser = authStatus.currentOrganization?.subscriptionTier === 'PRO';

  function handleUpgradeClick() {
    navigate('/billing');
  }

  useEffect(() => {
    loadIntegrations();
  }, []);

  async function loadIntegrations() {
    try {
      const response = await fetch('/api/integrations', {
        credentials: 'same-origin',
      });
      if (response.ok) {
        const data = await response.json();
        setIntegrations(data.integrations);
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page">
      <PageHeader title="Integrations" subtitle="Connect Yukon with your favorite tools" />

      <div className="page-content">
        {/* Loading State */}
        {loading && <LoadingSpinner message="Loading integrations..." />}

        {/* Integrations Grid */}
        {!loading && (
          <div className="integrations-grid">
            {integrations.map(integration => (
              <div
                key={integration.id}
                className={`integration-card ${integration.connected ? 'connected' : ''} ${integration.status === 'coming_soon' ? 'coming-soon' : ''}`}
                data-integration-id={integration.id}
              >
                <div className="integration-header">
                  <div className="integration-icon">
                    <i className={integration.icon}></i>
                  </div>
                  <div className="integration-status">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '4px',
                      }}
                    >
                      {integration.status === 'coming_soon' && (
                        <Badge variant="coming-soon">Coming Soon</Badge>
                      )}
                      {integration.status !== 'coming_soon' && !isProUser && (
                        <Badge variant="pro">
                          <i className="fa-solid fa-crown"></i> Pro Only
                        </Badge>
                      )}
                      {integration.connected && isProUser && (
                        <Badge variant="connected">
                          <i className="fa-solid fa-check-circle"></i> Connected
                        </Badge>
                      )}
                      {!integration.connected &&
                        integration.status === 'available' &&
                        isProUser && <Badge variant="available">Available</Badge>}
                      {integration.connected &&
                        integration.connectionDetails &&
                        integration.id === 'slack' &&
                        isProUser && (
                          <span
                            style={{
                              fontSize: '11px',
                              color: 'var(--text-secondary)',
                              fontWeight: 400,
                            }}
                          >
                            {integration.connectionDetails.workspaceName}
                          </span>
                        )}
                    </div>
                  </div>
                </div>

                <div className="integration-content">
                  <h3>{integration.name}</h3>
                  <p>{integration.description}</p>

                  {integration.connected &&
                    integration.connectionDetails &&
                    integration.id === 'webhooks' && (
                      <div className="connection-details">
                        <div className="detail-item">
                          <i className="fa-solid fa-check-circle"></i>
                          <span>{integration.connectionDetails.count} active webhook(s)</span>
                        </div>
                      </div>
                    )}

                  {integration.setupInstructions && (
                    <div className="setup-instructions">
                      <i className="fa-solid fa-info-circle"></i>
                      <span>{integration.setupInstructions}</span>
                    </div>
                  )}
                </div>

                <div className="integration-actions">
                  {integration.status === 'coming_soon' ? (
                    <Button
                      variant="secondary"
                      icon="fa-solid fa-clock"
                      disabled
                      style={{ cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      Coming Soon
                    </Button>
                  ) : !isProUser ? (
                    <Button variant="primary" icon="fa-solid fa-crown" onClick={handleUpgradeClick}>
                      Upgrade to Pro
                    </Button>
                  ) : (
                    <>
                      {/* Slack integration actions */}
                      {integration.id === 'slack' && (
                        <div>
                          {!integration.connected ? (
                            <a
                              href={`/slack/install?installation_id=${authStatus.currentInstallation || ''}`}
                            >
                              <img
                                alt="Add to Slack"
                                height="40"
                                width="139"
                                src="https://platform.slack-edge.com/img/add_to_slack.png"
                                srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
                              />
                            </a>
                          ) : (
                            <Button
                              variant="primary"
                              icon="fa-brands fa-slack"
                              as="a"
                              href={`https://app.slack.com/client/${integration.connectionDetails?.teamId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open in Slack
                            </Button>
                          )}
                        </div>
                      )}

                      {/* API integration actions */}
                      {integration.id === 'api' && (
                        <div>
                          <Button
                            variant="primary"
                            icon="fa-solid fa-book"
                            onClick={() => navigate('/api-docs')}
                          >
                            View Documentation
                          </Button>
                        </div>
                      )}

                      {/* CLI integration actions */}
                      {integration.id === 'cli' && (
                        <div>
                          <Button
                            variant="primary"
                            icon="fa-solid fa-download"
                            as="a"
                            href="#"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Install CLI
                          </Button>
                        </div>
                      )}

                      {/* Webhook integration actions */}
                      {integration.id === 'webhooks' && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <Button
                            variant="primary"
                            icon="fa-solid fa-cog"
                            onClick={() => navigate('/settings')}
                          >
                            Manage Webhooks
                          </Button>
                          <Button
                            variant="secondary"
                            icon="fa-solid fa-book"
                            onClick={() => navigate('/api-docs')}
                          >
                            View Docs
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
